'use client'

import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import type { App } from '@/payload-types'

function comparisonPath(slugs: string[]): string {
  const present = slugs.filter(Boolean)
  const normalized = new Set(present).size === 1 ? present.slice(0, 1) : present
  return normalized.length ? `/compare/${normalized.join('-vs-')}` : '/compare'
}

export function useComparisonApps(initialApps: App[]): [App[], Dispatch<SetStateAction<App[]>>] {
  const pathname = usePathname()
  const router = useRouter()
  const [apps, setApps] = useState(initialApps)
  const initialPath = comparisonPath(initialApps.map((app) => app.slug))
  const syncedPathRef = useRef(initialPath)
  const appsByPathRef = useRef(new Map([[initialPath, initialApps]]))
  const requestedPathRef = useRef<string | null>(null)

  useEffect(() => {
    // Read the browser URL so a late app fetch cannot undo a newer navigation.
    const currentPath = window.location.pathname
    const normalizedPath = currentPath.startsWith('/compare/')
      ? comparisonPath(currentPath.slice('/compare/'.length).replace(/\/$/, '').split('-vs-'))
      : currentPath
    if (currentPath !== normalizedPath) {
      if (requestedPathRef.current !== normalizedPath) {
        requestedPathRef.current = normalizedPath
        router.replace(normalizedPath, { scroll: false })
      }
      return
    }
    if (currentPath !== syncedPathRef.current) {
      const previousApps = appsByPathRef.current.get(currentPath)
      if (previousApps) {
        requestedPathRef.current = null
        syncedPathRef.current = currentPath
        setApps(previousApps)
      } else if (requestedPathRef.current !== currentPath) {
        // A history entry can outlive this component's app cache. Let the server
        // resolve it, including canonical ordering and comparison validation.
        requestedPathRef.current = currentPath
        router.replace(currentPath, { scroll: false })
      }
      return
    }

    const slugs = apps.map((app) => app.slug).filter(Boolean)
    if (slugs.length < 2) return

    const nextPath = comparisonPath(slugs)
    appsByPathRef.current.set(nextPath, apps)
    if (currentPath !== nextPath) {
      if (initialApps.length < 2) {
        if (requestedPathRef.current !== nextPath) {
          requestedPathRef.current = nextPath
          router.push(nextPath, { scroll: false })
        }
        return
      }
      requestedPathRef.current = null
      syncedPathRef.current = nextPath
      window.history.pushState(null, '', nextPath)
    }
  }, [apps, pathname, router, initialApps.length])

  return [apps, setApps]
}
